import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveIcon,
  TrophyIcon,
  CalendarIcon,
  AwardIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CompetitionsArchive = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  const fetchCompletedCompetitions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await api.get(`/competitions/my-competitions?page=${page}&limit=10`);
        if (response.data.success) {
          const completedCompetitions = response.data.data.competitions.filter((comp) => comp.status === 'completed');
          setCompetitions(completedCompetitions);
          setPagination(response.data.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching completed competitions:', error);
        toast.error(t('competitions.fetchError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchCompletedCompetitions(1);
  }, [fetchCompletedCompetitions]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const viewLeaderboard = (competitionId) => {
    navigate(`/leaderboard/${competitionId}`);
  };

  const page = pagination.page;
  const totalPages = Math.max(1, pagination.pages);

  return (
    <div
      className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <DailyExercisesHero
        title={t('competitions.archive')}
        description={t('competitions.archiveDescription')}
        icon={ArchiveIcon}
        iconClassName="text-amber-600"
      />

      {loading ? (
        <div className="rounded-[1.35rem] border-[3px] border-amber-200/80 bg-gradient-to-br from-white to-amber-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(245,158,11,0.1)]">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-amber-600" aria-hidden strokeWidth={2.25} />
          <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('competitions.archiveLoading')}</p>
        </div>
      ) : competitions.length === 0 ? (
        <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-gradient-to-br from-white to-amber-50/40 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(245,158,11,0.08)]">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.25rem] border-[3px] border-amber-200 bg-amber-50">
            <ArchiveIcon className="h-11 w-11 text-amber-500" strokeWidth={2} aria-hidden />
          </div>
          <p className="text-lg font-black text-slate-900 sm:text-xl">{t('competitions.noCompletedCompetitions')}</p>
          <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-relaxed text-slate-600 sm:text-base">
            {t('competitions.noCompletedCompetitionsDesc')}
          </p>
        </div>
      ) : (
        <>
          <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
            <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">
                {t('competitions.completedCompetitions')}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200/90 bg-slate-50/80">
                    <th
                      className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('competitions.competitionTitle')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.totalRounds')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.completedRounds')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.totalScore')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.competitionStart')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.competitionEnd')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {competitions.map((competition) => (
                    <tr key={competition.id} className="border-b border-slate-100 transition-colors hover:bg-sky-50/50">
                      <td className={`min-w-0 max-w-[14rem] px-3 py-3.5 sm:max-w-none ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-[3px] border-amber-200/80 bg-amber-50">
                            <TrophyIcon className="h-5 w-5 text-amber-600" strokeWidth={2.25} />
                          </div>
                          <div className="min-w-0">
                            <div className="break-words font-semibold text-slate-900">{competition.name}</div>
                            <div className="truncate text-xs text-slate-500">{competition.competitionId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3.5 text-center tabular-nums text-slate-800">
                        {competition.totalRounds}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3.5 text-center tabular-nums text-slate-800">
                        <span>{competition.myCompletedRounds ?? 0}</span>
                        <span className="text-slate-400"> / {competition.totalRounds}</span>
                      </td>
                      <td className="px-3 py-3.5 text-center">
                        <div className={`inline-flex items-center justify-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <AwardIcon className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />
                          <span className="text-lg font-black tabular-nums text-violet-700">{competition.myScore ?? '0.00'}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3.5 text-center text-xs text-slate-600">
                        <div className={`inline-flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CalendarIcon className="h-4 w-4 shrink-0 text-sky-500" aria-hidden />
                          {formatDate(competition.schedule?.competitionStart)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3.5 text-center text-xs text-slate-600">
                        <div className={`inline-flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CalendarIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                          {formatDate(competition.schedule?.competitionEnd)}
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-center">
                        <button
                          type="button"
                          onClick={() => viewLeaderboard(competition.competitionId)}
                          className="inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-xl border-2 border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-900 transition-colors hover:bg-violet-100 sm:text-sm"
                        >
                          <TrophyIcon className="h-4 w-4 shrink-0" aria-hidden />
                          {t('competitions.viewStatistics')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {totalPages > 1 && (
            <nav
              className="flex flex-col gap-3 rounded-[1.35rem] border-[3px] border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4 sm:px-6"
              aria-label={t('competitions.archivePaginationAria')}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => fetchCompletedCompetitions(page - 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isRTL ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronLeft className="h-4 w-4 shrink-0" />}
                {t('common.previous')}
              </button>
              <span className="text-center text-sm font-bold tabular-nums text-slate-600 sm:min-w-[12rem]">
                {t('competitions.archivePageOf', {
                  page: pagination.page,
                  pages: pagination.pages,
                  total: pagination.total,
                })}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => fetchCompletedCompetitions(page + 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('common.next')}
                {isRTL ? <ChevronLeft className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default CompetitionsArchive;
